import { deriveNaturalDecrementTrace } from '@/adapters/primitive/_pure/model/natural/decrement';
import { createNaturalMachineState, deriveNaturalTrace } from '@/adapters/primitive/_pure/model/natural/machine';
import type { NaturalMachineWheel, NaturalTrace } from '@/adapters/primitive/_contract/natural/machine';
import type { NaturalMultiplicationCycle, NaturalMultiplicationTrace } from '@/adapters/primitive/_contract/trace';
export type { NaturalMultiplicationCycle, NaturalMultiplicationTrace } from '@/adapters/primitive/_contract/trace';

function wheelValue(wheel: NaturalMachineWheel | undefined, radix: number): number {
    if (wheel === undefined) throw new Error('Natural multiplication trace is missing a receiver wheel.');
    return wheel.bands.reduce(
        (value, band) => value + band.countedSlots.length * radix ** band.power,
        0,
    );
}

function finalWheel(trace: NaturalTrace, role: NaturalMachineWheel['role']): NaturalMachineWheel | undefined {
    const state = trace.points.at(-1)?.nextState ?? trace.initialState;
    return state.wheels.find((wheel) => wheel.role === role);
}

function multiplierWheel(value: number, radix: number): NaturalMachineWheel {
    const state = createNaturalMachineState({
        operation: 'multiplication',
        radix,
        operands: [
            { role: 'multiplicand', value: 0 },
            { role: 'multiplier', value },
        ],
    });
    const wheel = state.wheels.find((candidate) => candidate.role === 'multiplier');
    if (wheel === undefined) throw new Error('Natural multiplication requires a Multiplier wheel.');
    return wheel;
}

export function deriveNaturalMultiplicationTrace(args: {
    multiplicand: number;
    multiplier: number;
    radix: number;
}): NaturalMultiplicationTrace {
    const cycles: NaturalMultiplicationCycle[] = [];
    let product = 0;
    let residual = args.multiplier;

    while (residual > 0) {
        const contributionTrace = deriveNaturalTrace({
            operation: 'addition',
            radix: args.radix,
            operandOne: product,
            operandTwo: args.multiplicand,
            operandSchedulingMode: 'grouped',
        }, { preResultInitialization: 'resident' });
        const decrementTrace = deriveNaturalDecrementTrace({
            receiverWheel: multiplierWheel(residual, args.radix),
            radix: args.radix,
            receiverInteraction: {
                receiver: { wheelRole: 'multiplier', power: 0 },
                interactionKind: 'subtractive',
            },
            unitId: `multiplier-unit-${cycles.length + 1}`,
        });
        const productAfter = wheelValue(finalWheel(contributionTrace, 'sum'), args.radix);
        const residualAfter = wheelValue(finalWheel(decrementTrace, 'multiplier'), args.radix);
        cycles.push({
            cycleIndex: cycles.length + 1,
            productBefore: product,
            productAfter,
            residualBefore: residual,
            residualAfter,
            contributionTrace,
            decrementTrace,
        });
        product = productAfter;
        residual = residualAfter;
    }

    return {
        multiplicand: args.multiplicand,
        multiplier: args.multiplier,
        cycles,
        product,
        residual,
    };
}